import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { House, ArrowRight, Wrench } from "@phosphor-icons/react";

/**
 * Tenant Portal Login — simple form: address + unit → server looks up the
 * Request Radar token for that unit → redirect to the portal.
 * URL: /tenant/login
 */
export default function TenantLogin() {
  const nav = useNavigate();
  const [address, setAddress] = useState("");
  const [unit, setUnit]       = useState("");
  const [busy, setBusy]       = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!address.trim()) { toast.error("Enter your address"); return; }
    setBusy(true);
    try {
      const r = await api.post("/public/tenant-portal-lookup",
        { address: address.trim(), unit: unit.trim() });
      const url = r.data?.portal_url;
      if (!url) throw new Error("No portal URL returned");
      // Portal URL is either absolute or relative — normalize to a client-side route
      const path = url.startsWith("http") ? new URL(url).pathname : url;
      toast.success("Opening your portal…");
      nav(path);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Address not found — contact your property manager"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1220] text-white flex flex-col" data-testid="tenant-login-page">
      <div className="flex-1 flex items-center justify-center p-5">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Logo size={30}/>
            <span className="font-display font-black text-2xl tracking-tight">Field Radar</span>
          </div>
          <div className="bg-white text-slate-900 p-6 border-2 border-cyan-500 rounded">
            <div className="flex items-center gap-2 mb-1">
              <House weight="fill" size={22} className="text-cyan-600"/>
              <h1 className="font-display font-black text-2xl tracking-tight">Resident Portal</h1>
            </div>
            <p className="text-sm text-slate-600 mb-5 leading-snug">
              Enter your address to open your maintenance portal. Submit new work orders, upload photos, and see repair history.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Street Address</label>
                <input value={address} onChange={e => setAddress(e.target.value)}
                  placeholder="5346 Chew Ave"
                  autoFocus
                  data-testid="tenant-login-address"
                  className="w-full h-12 px-3 border-2 border-slate-300 focus:border-cyan-500 focus:outline-none text-sm"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Unit (optional)</label>
                <input value={unit} onChange={e => setUnit(e.target.value)}
                  placeholder="8B"
                  data-testid="tenant-login-unit"
                  className="w-full h-12 px-3 border-2 border-slate-300 focus:border-cyan-500 focus:outline-none text-sm"/>
              </div>
              <button type="submit" disabled={busy}
                data-testid="tenant-login-submit"
                className="w-full h-12 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-400 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] inline-flex items-center justify-center gap-2 transition">
                {busy ? "Looking up…" : <>Open My Portal <ArrowRight weight="bold" size={16}/></>}
              </button>
            </form>
            <div className="mt-5 pt-4 border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
              <div className="flex items-start gap-2">
                <Wrench weight="fill" size={16} className="text-fuchsia-600 mt-0.5"/>
                <div>
                  <b>Don't see your portal?</b> Contact your property manager and ask for your Field Radar resident link. Your address must be onboarded before you can log in here.
                </div>
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-slate-400 mt-4">
            Landlord? Ask your property manager for your owner portal link.
          </div>
        </div>
      </div>
    </div>
  );
}
